import { Params } from 'next/dist/server/request/params';
import serverClient, { browserClient } from '@/lib/apolloClient';

import { normalizeTextString } from '../normalizeTextString';
import { calculateLanguageHandledPath } from './calculate-language-handled-path';
import { calculatePrefixPath } from './calculate-prefixed-path';

import {
  DictionarySubDirectories,
  getDictionarySlugData,
} from './get-url-subdirectories';
import { resolvePageBySlug } from '@/lib/resolvePageBySlug';
import { buildContentfulPageSlugQuery } from '@/lib/queries/getPageSlugsQuery';
import { resolveDetailPageBySlug } from '@/lib/resolveDetailPageBySlug';
import { isBrowser } from '@/helpers/is-browser';

// In the browser, route through the same-origin /api/graphql proxy so the
// Contentful token is never exposed. On the server, call Contentful directly.
const client = isBrowser ? browserClient : serverClient;

export type HreflangTag = {
  hreflang: string;
  href: string;
};

/* ------------------------------------------------------------------
   Helper: Create <link hreflang="xx" href="..." /> tags
-------------------------------------------------------------------*/
function createHrefLangTags(
  nodes: any[],
  dictionaryData: DictionarySubDirectories[],
  configPrefix?: string,
): HreflangTag[] {
  const prefix = configPrefix || '';
  return nodes.map((node) => {
    const locale = node.sys?.locale ?? '';

    const rawSlug = node.slug;

    const normalizedSlug = normalizeTextString(rawSlug, true);

    let href = '';

    if (dictionaryData?.length > 0) {
      href = calculateLanguageHandledPath(
        dictionaryData,
        locale,
        normalizedSlug,
        node.__typename,
        prefix,
      );
    } else {
      // fallback to simple prefix
      href = calculatePrefixPath(`/${locale}/${normalizedSlug}`, prefix);
    }

    return {
      hreflang: locale,
      href,
    };
  });
}

/* ------------------------------------------------------------------
   Helper: Fetch localized versions of a page for all languages
-------------------------------------------------------------------*/
async function fetchPageLocaleVariants(
  contentfulType: string,
  pageId: string,
  languages: string[],
): Promise<any[]> {
  const query = buildContentfulPageSlugQuery(contentfulType);

  const results = await Promise.all(
    languages.map(async (lang) => {
      const { data }: any = await client.query({
        query,
        variables: {
          id: pageId,
          locale: lang,
        },
        fetchPolicy: 'no-cache',
      });

      return data?.[contentfulType] ?? null;
    }),
  );

  // Flatten & remove nulls
  return results.filter(Boolean).flat();
}

/* ------------------------------------------------------------------
   Main: Build hreflang tags for all pages
-------------------------------------------------------------------*/
export const calculateHreflangTags = async (
  params: Params,
  languages: string[],
  config: any,
  preview: boolean,
  urlSubDirectoryCollection?: DictionarySubDirectories[],
): Promise<HreflangTag[]> => {
  const { slug, locale, detail_page } = params;

  let dictionaryData = [];
  // Load shared dictionary data only once
  if (urlSubDirectoryCollection && urlSubDirectoryCollection?.length > 0) {
    dictionaryData = urlSubDirectoryCollection;
  } else {
    dictionaryData = await getDictionarySlugData(languages, preview);
  }

  const configPrefix = config?.GATSBY_SITE_PATH_PREFIX || '';

  /* ---------------------------------------------------------------
     CASE 1: Detail pages → Not implemented
  ----------------------------------------------------------------*/
  if (locale && slug && detail_page) {
    const resolved = await resolveDetailPageBySlug({
      parent: slug as string,
      slug: detail_page as string,
      locale: locale as string,
      tagId: config?.MEETINGPLACE_TAG_CONTENTFUL_ID || '',
      config,
    });
    const pageId = resolved?.data?.sys?.id;
    const typename = resolved?.data?.__typename;

    if (!pageId || !typename) return [];
    // Convert Contentful __typename → Apollo field name
    const contentfulType = typename.charAt(0).toLowerCase() + typename.slice(1);
    const localizedNodes = await fetchPageLocaleVariants(
      contentfulType,
      pageId,
      languages,
    );

    return createHrefLangTags(localizedNodes, dictionaryData, configPrefix);
  }

  /* ---------------------------------------------------------------
     CASE 2: Pages with /[locale]/[slug] → editorial, listing, detail
  ----------------------------------------------------------------*/
  if (locale && slug) {
    const resolved = await resolvePageBySlug({
      slug: slug as string,
      locale: locale as string,
      tagId: config?.MEETINGPLACE_TAG_CONTENTFUL_ID || '',
      preview: preview,
    });

    const pageId = resolved?.data?.sys?.id;
    const typename = resolved?.data?.__typename;

    if (!pageId || !typename) return [];

    // Convert Contentful __typename → Apollo field name
    const contentfulType = typename.charAt(0).toLowerCase() + typename.slice(1);

    const localizedNodes = await fetchPageLocaleVariants(
      contentfulType,
      pageId,
      languages,
    );

    return createHrefLangTags(localizedNodes, dictionaryData, configPrefix);
  }

  /* ---------------------------------------------------------------
     CASE 3: Homepage /[locale]
  ----------------------------------------------------------------*/
  if (locale) {
    return languages.map((lang) => ({
      hreflang: lang,
      href: calculatePrefixPath(`/${lang}`, configPrefix),
    }));
  }

  return [];
};

